import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import { useMetrics } from '../hooks/useMetrics';
import { ComponentHealth } from '../components/Dashboard/ComponentHealth';
import { MetricsCard } from '../components/Dashboard/MetricsCard';
import { KafkaStatus } from '../components/Dashboard/KafkaStatus';
import { VenueControl } from '../components/Dashboard/VenueControl';
import { UsersTab, AccountsTab } from './Admin';
import { adminApi } from '../api/admin';
import type { SystemMetrics, OrderMetrics, KafkaMetrics } from '../types/metrics';
import type { SecurityMasterSyncResponse } from '../types/admin';

type DashboardTab = 'monitoring' | 'users' | 'accounts';

export function Dashboard() {
  const { logout, hasPermission } = useAuth();
  const canManageVenues = hasPermission('management');

  const [activeTab, setActiveTab] = useState<DashboardTab>('monitoring');
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResult, setSyncResult] = useState<SecurityMasterSyncResponse | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  const {
    data: systemMetrics,
    loading: systemLoading,
    error: systemError,
  } = useMetrics<SystemMetrics>('/api/metrics/health', 5000);

  const {
    data: orderMetrics,
    loading: orderLoading,
    error: orderError,
  } = useMetrics<OrderMetrics>('/api/metrics/orders', 5000);

  const {
    data: kafkaMetrics,
    loading: kafkaLoading,
    error: kafkaError,
  } = useMetrics<KafkaMetrics>('/api/metrics/kafka', 10000);

  const hasError = systemError || orderError || kafkaError;
  const isLoading = systemLoading && orderLoading && kafkaLoading;

  const handleSyncSecurityMaster = async () => {
    setSyncLoading(true);
    setSyncResult(null);
    setSyncError(null);

    try {
      const result = await adminApi.syncSecurityMaster();
      setSyncResult(result);
    } catch (err) {
      setSyncError(err instanceof Error ? err.message : 'Sync failed');
    } finally {
      setSyncLoading(false);
    }
  };

  return (
    <Layout>
      {/* Header Section */}
      <section className="bg-gradient-to-r from-primary-600 to-secondary-600 text-white py-12">
        <div className="container-custom">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold mb-2">System Dashboard</h1>
              <p className="text-primary-100">
                Real-time monitoring of system health and metrics
              </p>
            </div>
            <button
              onClick={logout}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-md text-sm font-medium transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </section>

      {/* Tab Navigation */}
      <div className="container-custom">
        <div className="border-b border-gray-200 mt-6">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('monitoring')}
              className={`pb-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === 'monitoring'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Monitoring
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`pb-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === 'users'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Users
            </button>
            <button
              onClick={() => setActiveTab('accounts')}
              className={`pb-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === 'accounts'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Accounts
            </button>
          </nav>
        </div>
      </div>

      {/* Monitoring Tab Content */}
      {activeTab === 'monitoring' && (
        <>
          {/* Error Display */}
          {hasError && (
            <div className="container-custom mt-8">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h3 className="text-red-800 font-semibold mb-2">Error Loading Metrics</h3>
                <p className="text-red-700 text-sm">
                  {systemError || orderError || kafkaError}
                </p>
                <p className="text-red-600 text-xs mt-2">
                  Your API key may be invalid or expired. Please log out and try again.
                </p>
              </div>
            </div>
          )}

          {/* Loading State */}
          {isLoading && !hasError && (
            <div className="container-custom mt-8">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                <p className="text-blue-800">Loading metrics...</p>
              </div>
            </div>
          )}

          {/* Dashboard Content */}
          {!isLoading && !hasError && (
            <div className="container-custom py-8 space-y-8">
              {/* Component Health Section */}
              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Component Health
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {systemMetrics && (
                    <>
                      <ComponentHealth
                        name="API Server"
                        healthy={systemMetrics.api_healthy}
                        uptime={systemMetrics.uptime_secs}
                      />
                      <ComponentHealth
                        name="OMS Engine"
                        healthy={systemMetrics.oms_healthy}
                      />
                      <ComponentHealth
                        name="Kafka Broker"
                        healthy={systemMetrics.kafka_healthy}
                      />
                      <ComponentHealth
                        name="Database"
                        healthy={systemMetrics.db_healthy}
                      />
                    </>
                  )}
                </div>
                {systemMetrics && (
                  <div className="mt-4 text-sm text-gray-600">
                    System Version: {systemMetrics.version}
                  </div>
                )}
              </section>

              {/* Venue Control Section - Only for Management users */}
              {canManageVenues && (
                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Venue Control
                  </h2>
                  <VenueControl />
                </section>
              )}

              {/* Admin Actions Section */}
              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Admin Actions
                </h2>
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        Security Master Sync
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Fetch and update tradable securities from RQD Clearing
                      </p>
                    </div>
                    <button
                      onClick={handleSyncSecurityMaster}
                      disabled={syncLoading}
                      className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
                    >
                      {syncLoading ? 'Syncing...' : 'Run Sync'}
                    </button>
                  </div>

                  {/* Sync Result */}
                  {syncResult && (
                    <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
                      <h4 className="text-green-800 font-semibold text-sm mb-2">Sync Complete</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Fetched:</span>{' '}
                          <span className="font-medium text-gray-900">{syncResult.total_fetched}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Inserted:</span>{' '}
                          <span className="font-medium text-green-700">{syncResult.inserted}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Updated:</span>{' '}
                          <span className="font-medium text-blue-700">{syncResult.updated}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Errors:</span>{' '}
                          <span className={`font-medium ${syncResult.errors > 0 ? 'text-red-700' : 'text-gray-900'}`}>
                            {syncResult.errors}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Sync Error */}
                  {syncError && (
                    <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                      <h4 className="text-red-800 font-semibold text-sm mb-1">Sync Failed</h4>
                      <p className="text-red-700 text-sm">{syncError}</p>
                    </div>
                  )}
                </div>
              </section>

              {/* Order Metrics Section */}
              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Order Metrics
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {orderMetrics && (
                    <>
                      <MetricsCard
                        title="Pending Requests"
                        value={orderMetrics.pending_requests}
                        icon="⏳"
                      />
                      <MetricsCard
                        title="Orders Today"
                        value={orderMetrics.orders_today}
                        icon="📋"
                      />
                      <MetricsCard
                        title="Fills Today"
                        value={orderMetrics.fills_today}
                        icon="✅"
                      />
                      <MetricsCard
                        title="Error Rate"
                        value={`${(orderMetrics.error_rate * 100).toFixed(2)}%`}
                        icon="⚠️"
                      />
                    </>
                  )}
                </div>
              </section>

              {/* Kafka Metrics Section */}
              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Kafka Metrics
                </h2>
                {kafkaMetrics && (
                  <KafkaStatus
                    consumerLag={kafkaMetrics.consumer_lag}
                    producerQueueSize={kafkaMetrics.producer_queue_size}
                  />
                )}
              </section>

              {/* Auto-Refresh Indicator */}
              <div className="text-center text-sm text-gray-500">
                <p>Dashboard auto-refreshes every 5 seconds</p>
                <p className="text-xs mt-1">
                  Last updated: {new Date().toLocaleTimeString()}
                </p>
              </div>
            </div>
          )}
        </>
      )}

      {/* Users Tab Content */}
      {activeTab === 'users' && (
        <div className="container-custom py-8">
          <UsersTab />
        </div>
      )}

      {/* Accounts Tab Content */}
      {activeTab === 'accounts' && (
        <div className="container-custom py-8">
          <AccountsTab />
        </div>
      )}
    </Layout>
  );
}
